import { useMemo } from "react";
import { CheckIcon } from "@heroicons/react/24/outline";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import {
  categorizeWatchlist,
  type NewsquawkCategory,
} from "@/pages/PortfolioBuilder/newsquawkCategories";
import type { Portfolio } from "@/types/Portfolio";
import { relevantCategories } from "../profileMapping";
import type { OnboardingAnswers } from "../types";

interface StepWatchlistsProps {
  answers: OnboardingAnswers;
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}

/**
 * Welcome flow step 2: pick starter watchlists from the Newsquawk prebuilt
 * library, most-relevant categories first. Selections are merged into the
 * primary headline wire as HIGHLIGHTS at apply time — they never filter the
 * feed, so there's no risk of over-narrowing a new user's view.
 */
export default function StepWatchlists({
  answers,
  selectedIds,
  onChange,
}: StepWatchlistsProps) {
  const { portfolios, portfoliosQuery } = usePortfoliosCRUD();

  const grouped = useMemo(() => {
    const prebuilt = portfolios.filter(
      (p: Portfolio) => p.owner_type === "NEWSQUAWK"
    );
    const byCategory = new Map<NewsquawkCategory, Portfolio[]>();
    for (const portfolio of prebuilt) {
      const category = categorizeWatchlist(portfolio);
      const list = byCategory.get(category) ?? [];
      list.push(portfolio);
      byCategory.set(category, list);
    }
    // Relevant categories (from profile answers) first, the rest after.
    const preferred = relevantCategories(answers.assetClasses);
    const ordered: { category: NewsquawkCategory; items: Portfolio[] }[] = [];
    for (const category of preferred) {
      const items = byCategory.get(category);
      if (items?.length) {
        ordered.push({ category, items });
        byCategory.delete(category);
      }
    }
    for (const [category, items] of byCategory) {
      if (items.length) ordered.push({ category, items });
    }
    return ordered;
  }, [portfolios, answers.assetClasses]);

  const toggle = (id: number) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((x) => x !== id)
        : [...selectedIds, id]
    );
  };

  return (
    <div>
      <h3 className="text-base font-semibold text-gray-100">
        Highlight your names
      </h3>
      <p className="mt-1 text-sm text-gray-400">
        Pick watchlists to visually highlight in your headline feed. The full
        wire keeps flowing — your names just stand out. You can build your own
        later.
      </p>

      {portfoliosQuery.isLoading && (
        <p className="mt-4 text-sm text-gray-500 animate-pulse">
          Loading the Newsquawk library…
        </p>
      )}

      {!portfoliosQuery.isLoading && grouped.length === 0 && (
        <p className="mt-4 text-sm text-gray-500">
          No prebuilt watchlists available right now — you can skip this step.
        </p>
      )}

      <div className="mt-4 max-h-72 overflow-y-auto pr-1 space-y-4">
        {grouped.map(({ category, items }) => (
          <div key={category}>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {category}
            </div>
            <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {items.map((portfolio) => {
                const selected = selectedIds.includes(portfolio.id);
                return (
                  <button
                    key={portfolio.id}
                    type="button"
                    onClick={() => toggle(portfolio.id)}
                    aria-pressed={selected}
                    className={`flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors cursor-pointer ${
                      selected
                        ? "border-squawk-blue bg-squawk-blue/10 text-squawk-blue"
                        : "border-gray-700 bg-gray-900/50 text-gray-200 hover:border-gray-500"
                    }`}
                  >
                    <span className="truncate">{portfolio.name}</span>
                    {selected && <CheckIcon className="h-4 w-4 shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
